import React, { useCallback, useEffect, useState } from "react";
import { Megaphone, Plus, Trash2, Send, Clock, CalendarClock, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const KIND_OPTIONS = [
  { key: "discord", label: "Discord webhook", hint: "Discord · Server Settings → Integrations → Webhooks → Copy URL" },
  { key: "slack",   label: "Slack webhook",   hint: "Slack App → Incoming Webhooks → Copy URL" },
  { key: "zapier",  label: "Zapier catch hook",hint: "Zapier Zap → Catch Hook trigger → Copy webhook URL" },
  { key: "make",    label: "Make.com webhook", hint: "Make scenario → Webhooks module → Copy URL" },
  { key: "webhook", label: "Generic POST",     hint: "Any endpoint that accepts JSON { text, show_name, host_name, listen_url }" },
];

/**
 * AnnouncementsPanel — pre-configure a cross-post message + webhook
 * destinations. Every time the host hits "Go Live Everywhere" in the
 * RestreamPanel, the backend fan-outs the rendered message to each hook.
 *
 * Placeholders in the template: {show_name} {host_name} {station} {listen_url}
 */
export default function AnnouncementsPanel({ meta, defaultOpen = false }) {
  const [cfg, setCfg] = useState(null);
  const [hooks, setHooks] = useState([]);
  const [runs, setRuns] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(defaultOpen);
  const [showRuns, setShowRuns] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ label: "", kind: "discord", url: "" });
  const [savingCfg, setSavingCfg] = useState(false);
  const [schedMin, setSchedMin] = useState(30);
  const [schedMsg, setSchedMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, h, s] = await Promise.all([
        api.get("/radio/announcements/config"),
        api.get("/radio/announcements/hooks"),
        api.get("/radio/announcements/schedule"),
      ]);
      setCfg(c.data); setHooks(h.data || []); setScheduled(s.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  const loadRuns = async () => {
    try { const { data } = await api.get("/radio/announcements/runs"); setRuns(data || []); } catch {}
  };
  useEffect(() => { load(); }, [load]);

  const createSchedule = async () => {
    const mins = parseInt(schedMin);
    if (!mins || mins < 1) { toast.error("Minutes from now must be ≥ 1"); return; }
    const when = new Date(Date.now() + mins * 60_000).toISOString();
    try {
      await api.post("/radio/announcements/schedule", {
        scheduled_at: when,
        message: schedMsg.trim() || null,
        show_name: meta?.show_name || null,
        listen_url: meta?.listen_url || null,
        station_slug: meta?.station || null,
      });
      toast.success(`Scheduled · will fire in ${mins} min`);
      setSchedMsg(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancelSchedule = async (sid) => {
    try { await api.delete(`/radio/announcements/schedule/${sid}`); toast.success("Scheduled announcement cancelled"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const { data } = await api.post("/radio/announcements/config", { template: cfg.template, auto_post: cfg.auto_post });
      setCfg(data); toast.success("Template saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingCfg(false); }
  };
  const addHook = async () => {
    if (!form.label || !form.url) { toast.error("Fill label + URL"); return; }
    try {
      await api.post("/radio/announcements/hooks", { ...form, enabled: true });
      setForm({ label: "", kind: "discord", url: "" }); setAdding(false); load();
      toast.success("Webhook added");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const toggleHook = async (h) => {
    try { await api.patch(`/radio/announcements/hooks/${h.id}`, { enabled: !h.enabled }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteHook = async (h) => {
    if (!window.confirm(`Delete "${h.label}"?`)) return;
    try { await api.delete(`/radio/announcements/hooks/${h.id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const testFire = async () => {
    try {
      const { data } = await api.post("/radio/announcements/test");
      const ok = (data.results || []).filter((r) => r.ok).length;
      const fail = (data.count || 0) - ok;
      toast.success(`Test fired: ${ok} OK${fail ? ` · ${fail} failed` : ""}`);
      if (showRuns) loadRuns();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-white/50 text-xs">Loading announcements…</div>;

  const enabledHooks = hooks.filter(h => h.enabled).length;

  return (
    <div data-testid="announcements-panel" className="rounded-lg border border-white/10 bg-black/30">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        <Megaphone className="w-3.5 h-3.5 text-yellow-300" />
        <div className="flex-1 min-w-0">
          <div className="text-white font-black text-[11px] uppercase tracking-wider">Cross-post announcements</div>
          <div className="text-white/50 text-[10px] truncate">
            {enabledHooks > 0 ? `${enabledHooks} webhook${enabledHooks > 1 ? "s" : ""} ready · auto-posts on Go Live` : "Set up Discord / Slack / Zapier hooks to auto-announce"}
          </div>
        </div>
        {cfg?.auto_post && enabledHooks > 0 && <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 text-[9px] font-black px-2 py-0.5 rounded-full">ON</span>}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          {/* Template editor */}
          <div className="bg-black/40 rounded border border-white/10 p-2 space-y-1.5">
            <div className="text-[9px] uppercase tracking-wider font-black text-white/60">Message template</div>
            <textarea
              data-testid="announcements-template"
              value={cfg?.template || ""}
              onChange={(e) => setCfg({ ...cfg, template: e.target.value })}
              rows={3}
              className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[11px] resize-none"
            />
            <div className="text-white/40 text-[9px]">
              Placeholders: <code>{"{show_name}"}</code> <code>{"{host_name}"}</code> <code>{"{station}"}</code> <code>{"{listen_url}"}</code>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-[10px] text-white/70 flex-1">
                <input
                  type="checkbox" data-testid="announcements-autopost"
                  checked={!!cfg?.auto_post}
                  onChange={(e) => setCfg({ ...cfg, auto_post: e.target.checked })}
                  className="accent-yellow-400"
                />
                Auto-post when I hit Go Live
              </label>
              <button
                data-testid="announcements-save-cfg"
                onClick={saveCfg}
                disabled={savingCfg}
                className="bg-yellow-400 hover:bg-yellow-300 disabled:opacity-40 text-black font-black text-[10px] px-2 py-1 rounded"
              >{savingCfg ? "…" : "Save"}</button>
            </div>
          </div>

          {/* Hooks list */}
          <div className="space-y-1" data-testid="announcements-hooks">
            {hooks.length === 0 && (
              <div className="text-white/40 text-[10px] text-center py-2">
                No webhooks yet. Add Discord / Slack / Zapier below to auto-announce your shows.
              </div>
            )}
            {hooks.map((h) => (
              <div key={h.id} data-testid={`annc-hook-${h.id}`} className="bg-white/5 rounded p-1.5 flex items-center gap-2">
                <input
                  type="checkbox" checked={!!h.enabled} onChange={() => toggleHook(h)}
                  className="accent-yellow-400"
                  data-testid={`annc-toggle-${h.id}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-[10px] font-bold truncate">{h.label}</div>
                  <div className="text-white/50 text-[9px] truncate">{h.kind} · {h.url}</div>
                </div>
                <button onClick={() => deleteHook(h)} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 p-1 rounded">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          {/* Add form */}
          {adding ? (
            <div className="bg-black/40 border border-white/10 rounded p-2 space-y-1.5" data-testid="annc-add-form">
              <input
                data-testid="annc-add-label"
                placeholder="Label (e.g. 'My Discord fans')"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[10px]"
              />
              <select
                data-testid="annc-add-kind"
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[10px]"
              >
                {KIND_OPTIONS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
              <div className="text-white/50 text-[9px]">
                {KIND_OPTIONS.find(k => k.key === form.kind)?.hint}
              </div>
              <input
                data-testid="annc-add-url"
                placeholder="Webhook URL"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[10px] font-mono"
              />
              <div className="flex gap-1.5">
                <button
                  data-testid="annc-add-save"
                  onClick={addHook}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-black text-[10px] py-1.5 rounded"
                >Save webhook</button>
                <button
                  onClick={() => setAdding(false)}
                  className="bg-white/5 hover:bg-white/15 text-white font-black text-[10px] py-1.5 px-3 rounded"
                >Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex gap-1">
              <button
                data-testid="annc-add-open"
                onClick={() => setAdding(true)}
                className="flex-1 inline-flex items-center justify-center gap-1 bg-white/5 hover:bg-white/15 text-white/80 font-black text-[10px] py-1.5 rounded border border-dashed border-white/20"
              >
                <Plus className="w-3 h-3" /> Add webhook
              </button>
              <button
                data-testid="annc-test"
                onClick={testFire}
                disabled={enabledHooks === 0}
                className="inline-flex items-center gap-1 bg-yellow-400/20 hover:bg-yellow-400/40 disabled:opacity-30 text-yellow-100 font-black text-[10px] py-1.5 px-2 rounded"
              ><Send className="w-3 h-3" /> Test fire</button>
              <button
                data-testid="annc-runs-toggle"
                onClick={() => { setShowRuns(v => !v); if (!showRuns) loadRuns(); }}
                className="inline-flex items-center gap-1 bg-white/5 hover:bg-white/15 text-white/80 font-black text-[10px] py-1.5 px-2 rounded"
              ><Clock className="w-3 h-3" /> History</button>
            </div>
          )}

          {showRuns && (
            <div className="space-y-1 max-h-[160px] overflow-y-auto" data-testid="annc-runs">
              {runs.length === 0 && <div className="text-white/40 text-[10px] text-center py-2">No announcements fired yet.</div>}
              {runs.map((r) => (
                <div key={r.id} className="bg-white/5 rounded p-1.5">
                  <div className="text-white/50 text-[9px] font-mono">{new Date(r.fired_at).toLocaleString()}</div>
                  <div className="text-white text-[10px] truncate">{r.rendered}</div>
                  <div className="flex gap-1 mt-0.5">
                    {(r.results || []).map((res, i) => (
                      <span key={i} className={`text-[8px] font-black px-1 rounded ${res.ok ? "bg-emerald-500/30 text-emerald-200" : "bg-red-500/30 text-red-200"}`}>
                        {res.ok ? `${res.status}` : "ERR"}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Scheduled announcements */}
          <div className="bg-black/40 rounded border border-white/10 p-2 space-y-1.5" data-testid="annc-schedule-block">
            <div className="flex items-center gap-1.5">
              <CalendarClock className="w-3 h-3 text-sky-400" />
              <div className="text-[9px] uppercase tracking-wider font-black text-white/60 flex-1">Schedule a hype post</div>
            </div>
            <div className="flex items-center gap-1.5">
              <input
                data-testid="annc-sched-min"
                type="number" min="1" max="720"
                value={schedMin}
                onChange={(e) => setSchedMin(e.target.value)}
                className="w-16 bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[10px]"
              />
              <span className="text-white/60 text-[10px]">minutes from now</span>
              <button
                data-testid="annc-sched-create"
                onClick={createSchedule}
                className="ml-auto bg-sky-500 hover:bg-sky-400 text-black font-black text-[10px] px-2 py-1 rounded"
              >Schedule</button>
            </div>
            <input
              data-testid="annc-sched-message"
              placeholder="Custom message (leave blank to use template)"
              value={schedMsg}
              onChange={(e) => setSchedMsg(e.target.value)}
              className="w-full bg-black/50 border border-white/10 rounded px-2 py-1 text-white text-[10px]"
            />
            <div className="text-white/50 text-[9px] italic">
              Fires to your personal webhooks AND Tap IN TV network webhooks (if configured). Perfect for "Going live in 30 min" hype posts.
            </div>
            {scheduled.filter(s => s.status === "pending").length > 0 && (
              <div className="space-y-1 pt-1" data-testid="annc-sched-pending">
                {scheduled.filter(s => s.status === "pending").map((s) => (
                  <div key={s.id} data-testid={`annc-sched-${s.id}`} className="flex items-center gap-1.5 bg-sky-500/10 border border-sky-500/30 rounded p-1">
                    <Clock className="w-3 h-3 text-sky-300 shrink-0" />
                    <div className="flex-1 min-w-0 text-[10px]">
                      <div className="text-sky-100 font-bold">{new Date(s.scheduled_at).toLocaleString()}</div>
                      <div className="text-white/60 text-[9px] truncate">{s.message || s.show_name || "Template"}</div>
                    </div>
                    <button onClick={() => cancelSchedule(s.id)} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 p-1 rounded">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
